import React from 'react';
import { MessageCircle } from 'lucide-react';

export const LandingHero = ({ navigate, setShowInquiryForm, t }) => (
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-red-200/30 rounded-full blur-3xl"></div>
        
        <div className="mx-auto max-w-7xl relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-full border border-orange-200 mb-6">
                <span className="text-2xl">🇮🇳</span>
                <span className="text-sm font-medium text-gray-700">{t('landing.heroTag')}</span>
              </div>
              
              <h1 className="font-heading text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t('landing.heroTitle1')}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">{t('landing.heroTitle2')}</span>
                {' '}{t('landing.heroTitle3')}
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('landing.heroSubtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <button
                  onClick={() => setShowInquiryForm(true)}
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                  data-testid="hero-cta-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  Get Free Consultation
                </button>
                <button
                  onClick={() => navigate('/treatments')}
                  className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold text-lg border-2 border-orange-200 hover:border-orange-400 transition-all"
                >
                  {t('landing.ctaExplore')} →
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{t('landing.isoCertified')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{t('landing.nabhAccredited')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{t('landing.jciStandards')}</span>
                </div>
              </div>
            </div>

            {/* Hero Image/Stats Card */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-orange-100">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-4xl">🏥</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      {t('landing.activeNow')}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('landing.platformHighlights')}
                  </h3>
                  <p className="text-gray-600 text-sm">{t('landing.realTimeStats')}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-2xl border border-orange-100">
                    <div className="text-3xl font-bold text-orange-600 mb-1">2,500+</div>
                    <div className="text-sm text-gray-600">{t('landing.verifiedDoctors')}</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border border-blue-100">
                    <div className="text-3xl font-bold text-blue-600 mb-1">450+</div>
                    <div className="text-sm text-gray-600">{t('landing.partnerHospitals')}</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100">
                    <div className="text-3xl font-bold text-green-600 mb-1">98%</div>
                    <div className="text-sm text-gray-600">{t('landing.successRate')}</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100">
                    <div className="text-3xl font-bold text-purple-600 mb-1">4.9⭐</div>
                    <div className="text-sm text-gray-600">{t('landing.patientRating')}</div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 border-2 border-white"></div>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 border-2 border-white"></div>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 border-2 border-white"></div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">1,200+</span> {t('landing.patientsTreated')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Pattern */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-orange-200 to-red-300 rounded-3xl -z-10 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>
);
